import { useEffect } from 'react';
import { getDefaultStore, useSetAtom } from 'jotai';
import { mapOverlaysAtom, type MapOverlay } from './atoms';

/**
 * The app's store, for state that's app-wide rather than the nearest `<Provider>`'s: a
 * map under a page's own `<Provider>` still draws the overlays and pins the app has.
 */
export const appStore = { store: getDefaultStore() };

/**
 * Draws a component on every map for as long as the caller is mounted — markers, say,
 * that belong on whichever map is on screen rather than on one page's.
 *
 * The control slot's sibling: a feature registers an overlay once, from anywhere in
 * the app, and each `<Map>` renders every registered overlay inside itself. The map
 * still depends on none of them.
 *
 * @param {MapOverlay} overlay - The overlay to contribute. Pass a component defined
 *   at module scope, so the registration holds still across renders.
 */
export const useMapOverlay = ({ id, component }: MapOverlay): void => {
  const setOverlays = useSetAtom(mapOverlaysAtom, appStore);

  useEffect(() => {
    setOverlays((overlays) => [
      ...overlays.filter((overlay) => overlay.id !== id),
      { id, component },
    ]);
    return () =>
      setOverlays((overlays) =>
        overlays.filter((overlay) => overlay.id !== id)
      );
  }, [id, component, setOverlays]);
};
